import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import {
  GenericTimelyAccountId,
  GenericTimelyUserId,
  VariousMemoryIdRefsSchema,
  YYYYMMDDSchema,
} from '../models.generic';
import { IntervalPredictionEnumSchema } from './models.ai_prediction_logs';

extendZodWithOpenApi(z);

/**
 * Side effects of e.g coercing a tool config
 *
 * @see coerceToolConfigSideEffects
 * @see coerceMemoryConfigSideEffects
 */
export type AiSideEffects = {
  aiModelMustBeRetrained: boolean;
  aiSuggestionsMustBeRefreshed: boolean;
};

export const PredictionSchema = z.object({
  timely_project_id: z
    .number()
    .describe('The Timely `project.id` most likely to apply')
    .int()
    .openapi({ example: 1234 }),
  timely_label_ids: z
    .array(z.number().describe('The Timely `label.id`s most likely to apply').int())
    .openapi({
      example: [32, 11],
      description:
        'One ID (if only predicting parent tag), or two IDs if predicting to a child label_id',
    })
    .default([])
    .optional(),
  memory_ids: z
    .array(z.number().or(VariousMemoryIdRefsSchema))
    .openapi({
      example: [1234, 5678, 'group_tic_123'],
      description: 'Memory IDs that are most likely to apply to this prediction',
    })
    .default([])
    .optional(),
  confidence: z
    .number()
    .describe('Confidence of prediction')
    .min(0)
    .max(1)
    .optional()
    .openapi({ example: 0.9 }),
  note: z.string().max(2048).optional().nullable().openapi({
    description: 'Suggested note to use for time entry',
    example: 'Meeting to discuss project X',
  }),
  valid_as_hour: z.boolean().optional().default(false).openapi({
    description: 'If true, this prediction has all the required data to create a time entry.',
    example: true,
  }),
});

export const PredictedIntervalSchema = z.object({
  started_at: z
    .string()
    .datetime({ offset: true })
    .describe('Interval starts at')
    .openapi({ example: '2023-10-25T10:00:00.000Z' }),
  ended_at: z
    .string()
    .datetime({ offset: true })
    .describe('Interval ends at')
    .openapi({ example: '2023-10-25T10:05:00.000Z' }),
  predictions: z.array(PredictionSchema),
});

export type CreateTimeIntervalPredictions = z.infer<typeof CreateTimeIntervalPredictionsSchema>;

export const CreateTimeIntervalPredictionsSchema = z.object({
  timely_account_id: GenericTimelyAccountId,
  timely_user_id: GenericTimelyUserId,
  range_started_at: z.string().datetime({ offset: true }).optional().openapi({
    description: 'Earliest date of the range of predictions. Inferred from `intervals` by default.',
    example: '2024-02-06T00:00:00.000Z',
  }),
  range_ended_at: z.string().datetime({ offset: true }).optional().openapi({
    description: 'Specify end of range. Inferred from `intervals` by default.',
    example: '2024-02-06T23:59:59.000Z',
  }),

  /** @todo Fix typo, but everywhere -- must be in-sync with AI deploy  */
  infered_dates: z
    .array(YYYYMMDDSchema)
    .describe(
      '_Every_ YYYY-MM-DD date in the range that TIC requested predictions for MUST be returned, even if no predictions were created'
    ),
  model_last_trained_at: z.string().datetime({ offset: true }).optional().openapi({
    description: 'When was the AI model last trained?',
    example: '2024-02-06T23:59:59.000Z',
  }),
  event: IntervalPredictionEnumSchema.default('inference_complete').openapi({
    description: 'How did the last inference go?',
    example: 'inference_complete',
  }),
  message: z
    .string()
    .optional()
    .openapi({ description: 'Optional message to describe state of predictions' }),
  intervals: z.array(PredictedIntervalSchema).openapi({
    description: 'Predicted intervals of time, sorted by earliest to latest `started_at`',
  }),
});
export const AiPredictionAsDatesUpdatedAtSchema = z
  .record(z.string(), z.string().datetime({ offset: true }))
  .openapi({
    description: 'When was the last prediction for each day in the range?',
    example: { '2024-02-06': '2024-02-06T22:32:59.000Z' },
  });
